/** Production Canvas — top-left navigation bar
 * (VISTOERA_CONTENT_PROJECT_DIRECT_CANVAS_AND_FULLSCREEN_CANVAS_SHELL_V1
 * §16–§22). Replaces the previous top-left zoom/fit/minimap-toggle
 * button group (now redundant with the bottom-left React Flow
 * `Controls` — see `CanvasBoard.tsx`) with exactly:
 * `[ ← ]  [ Recent Projects ]  [ Project Name (click-to-rename) ]`. */

import { ClockCircleOutlined, CopyOutlined, DeleteOutlined, FileAddOutlined, LeftOutlined, MoreOutlined } from '@ant-design/icons'
import { Input, Modal, Popover, Spin, message, type InputRef, type MenuProps } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { listContentProjects, type ContentProjectSummary, type SaasApiError } from '../../../api'
import { formatDateTime } from '../../../../i18n/dateFormat'
import { useUiLocale } from '../../../../i18n/LocaleProvider'
import { VistoeraActionMenu } from '../../../ui/action'
import {
  PROJECT_ACTION_MENU_ITEM_KEYS,
  canConfirmDeleteProject,
  canStartCreateProject,
  canStartDuplicateProject,
  resolveProjectActionMenuClickTarget,
  resolveProjectActionMenuTriggerBusy,
} from './canvasProjectActionMenuContract'

interface Props {
  workspaceId: string
  projectId: string
  projectTitle: string
  /** Persists to backend; throws on failure so this component can
   * revert the optimistic input value (spec §22). */
  onRenameProject: (title: string) => Promise<void>
  /** 复制项目 (VISTOERA_CONTENT_CANVAS_MEDIA_CARD_AND_GENERATION_INTERACTION_V2
   * §41) — clones the whole project server-side and navigates to the
   * new project's own Canvas; owned by `ProductionCanvasPage`, this
   * component only renders the trigger + its own busy state. */
  onDuplicateProject: () => Promise<void>
  /** VistoeraTASK-00250 — "项目操作" menu's other two actions. Both are
   * thin passthroughs owned by `ProductionCanvasPage` (canonical
   * create/delete API call + navigate); this component only renders the
   * menu item + confirmation UI. */
  onCreateProject: () => Promise<void>
  onDeleteProject: () => Promise<void>
  /** TASK-00556 — READ_ONLY Canvas (another member's shared project): no
   * rename / delete, the project menu only offers "复制项目". */
  readOnly?: boolean
}

const RECENT_PROJECTS_LIMIT = 8
const MAX_TITLE_LENGTH = 300

export default function CanvasTopBar({
  workspaceId,
  projectId,
  projectTitle,
  onRenameProject,
  onDuplicateProject,
  onCreateProject,
  onDeleteProject,
  readOnly = false,
}: Props) {
  const navigate = useNavigate()
  const { t } = useTranslation(['canvas', 'contentProjects', 'actions'])
  const { locale } = useUiLocale()

  const [renaming, setRenaming] = useState(false)
  const [draftTitle, setDraftTitle] = useState(projectTitle)
  const [savingTitle, setSavingTitle] = useState(false)
  const [duplicating, setDuplicating] = useState(false)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  /** VistoeraTASK-00250 §十三 — ref-based synchronous guard (belt-and-
   * suspenders alongside `Modal.confirm`'s own built-in
   * pending-disable-OK-button behavior) so a delete confirm can never be
   * re-entered twice even within the same tick. Mirrors the same
   * `submittingRef` pattern `VistoeraActionConfirm` already uses. */
  const deletingRef = useRef(false)
  const inputRef = useRef<InputRef | null>(null)

  /** VistoeraTASK-00250 §十八/CASE C — antd `Dropdown`'s own internal
   * Escape handling only fires when DOM focus is actually inside the
   * popup; the trigger button itself keeps focus after opening, so a
   * bare uncontrolled Dropdown never sees Escape here and
   * `CanvasBoard.tsx`'s own global keydown handler (which is unaware of
   * open dropdowns — it only special-cases `.ant-modal-wrap`) doesn't
   * close it either, verified live against the deployed Preview build.
   * Fix: own the `open` state ourselves and close it via a capture-phase
   * listener registered only while open (see effect below) — this ALSO
   * makes CASE B (click-outside) and the on-item-click auto-close still
   * work via `onOpenChange`/explicit `setMenuOpen(false)` respectively. */
  const [menuOpen, setMenuOpen] = useState(false)
  const [recentOpen, setRecentOpen] = useState(false)
  const [recentLoading, setRecentLoading] = useState(false)
  const [recentItems, setRecentItems] = useState<ContentProjectSummary[]>([])
  const [recentLoaded, setRecentLoaded] = useState(false)

  useEffect(() => {
    if (!renaming) setDraftTitle(projectTitle)
  }, [projectTitle, renaming])

  /** VistoeraTASK-00250 §十八/CASE C/CASE O — closes the project-action
   * menu on Escape (see `menuOpen` comment above for why antd's own
   * handling + CanvasBoard's global handler both miss this), and
   * `stopPropagation`s so CanvasBoard's OWN Escape handling (closing a
   * node context menu / collapsing an expanded node card) never ALSO
   * fires from the same keypress — one Escape press while this menu is
   * open must do exactly one thing: close this menu, never leak into
   * unrelated Canvas keyboard shortcuts. Capture-phase so it runs
   * before CanvasBoard's bubble-phase `window` listener; only
   * registered while the menu is actually open (never a permanent
   * global listener). */
  useEffect(() => {
    if (!menuOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setMenuOpen(false)
      }
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [menuOpen])

  const goToProjectList = () => {
    // Canonical navigate(PROJECT_LIST_ROUTE) — never browser history
    // (spec §17): stable even if the user deep-linked straight into
    // this Canvas URL with no "back" history entry to speak of.
    navigate(`/workspace/${workspaceId}/content-production`)
  }

  const loadRecent = async () => {
    setRecentLoading(true)
    try {
      // Reuses the SAME list endpoint as the Project List page — already
      // scoped to this workspace/tenant and already sorted
      // `updated_at DESC` (spec §19), so no new backend surface needed.
      const { data } = await listContentProjects(workspaceId, { page: 1, page_size: RECENT_PROJECTS_LIMIT })
      setRecentItems(data.items)
      setRecentLoaded(true)
    } catch (e) {
      message.error((e as SaasApiError).message || t('chrome.topBar.recentProjectsLoadFailed'))
    } finally {
      setRecentLoading(false)
    }
  }

  const startRename = () => {
    if (readOnly) return
    setDraftTitle(projectTitle)
    setRenaming(true)
    window.setTimeout(() => inputRef.current?.focus(), 0)
  }

  const commitRename = async () => {
    const clean = draftTitle.trim()
    if (!clean) {
      message.error(t('chrome.topBar.renameEmpty'))
      setDraftTitle(projectTitle)
      setRenaming(false)
      return
    }
    if (clean === projectTitle) {
      setRenaming(false)
      return
    }
    setSavingTitle(true)
    try {
      await onRenameProject(clean)
      setRenaming(false)
    } catch (e) {
      message.error((e as SaasApiError).message || t('chrome.topBar.renameFailed'))
      setDraftTitle(projectTitle)
    } finally {
      setSavingTitle(false)
    }
  }

  const cancelRename = () => {
    setDraftTitle(projectTitle)
    setRenaming(false)
  }

  const handleDuplicateProject = async () => {
    if (!canStartDuplicateProject(duplicating)) return
    setDuplicating(true)
    try {
      await onDuplicateProject()
      // Success navigates away to the new project's own Canvas — no
      // local state to reset (spec §41 "复制完成后：自动进入：新项目
      // Canvas"), so `finally` below only needs to cover the error path.
    } catch (e) {
      message.error((e as SaasApiError).message || t('chrome.topBar.duplicateProjectFailed'))
    } finally {
      setDuplicating(false)
    }
  }

  /** VistoeraTASK-00250 §六/§七 — "新建项目" menu item. Same
   * loading/error-toast/double-submit-guard shape as
   * `handleDuplicateProject` above; success navigates away (new
   * project's own Canvas) so there is nothing to reset on the happy
   * path, and failure leaves this project instance fully untouched. */
  const handleCreateProject = async () => {
    if (readOnly) return
    if (!canStartCreateProject(creating)) return
    setCreating(true)
    try {
      await onCreateProject()
    } catch (e) {
      message.error((e as SaasApiError).message || t('chrome.topBar.createProjectFailed'))
    } finally {
      setCreating(false)
    }
  }

  /** VistoeraTASK-00250 §十一/§十二/§十三 — "删除当前项目" menu item.
   * The dropdown menu has already closed itself (antd's default
   * item-click behavior) by the time this fires; a SEPARATE
   * `Modal.confirm` (not an anchored popover — the trigger button's own
   * anchor context is gone once the menu closes) is the second,
   * mandatory confirmation. Reuses the EXACT canonical
   * `contentProjects:deleteConfirm.*` copy the Project List page's own
   * per-card delete already uses — including its explicit
   * non-recoverability wording — and interpolates the REAL current
   * project title so the user unambiguously knows which project is
   * about to be deleted (never a generic "确认删除？"). Rethrows on
   * failure (matching `ContentProductionListPage.tsx`'s `onDelete`
   * pattern, not `onBatchDelete`'s swallow-and-continue) so
   * `Modal.confirm` keeps itself open and re-enables its OK button. */
  const handleDeleteProject = () => {
    if (readOnly) return
    if (!canConfirmDeleteProject(deletingRef.current)) return
    Modal.confirm({
      title: t('contentProjects:deleteConfirm.title'),
      content: t('contentProjects:deleteConfirm.description', { name: projectTitle }),
      okText: t('contentProjects:deleteConfirm.okText'),
      okType: 'danger',
      cancelText: t('actions:cancel'),
      onOk: async () => {
        if (!canConfirmDeleteProject(deletingRef.current)) return
        deletingRef.current = true
        setDeleting(true)
        try {
          await onDeleteProject()
          // Success navigates away (PROJECT_LIST_ROUTE) — this whole
          // component unmounts as part of that route change, so there
          // is no local state left to reset on the happy path.
        } catch (e) {
          message.error((e as SaasApiError).message || t('chrome.topBar.deleteProjectFailed'))
          deletingRef.current = false
          setDeleting(false)
          // Rethrow: Modal.confirm's own promise-based onOk contract
          // keeps the modal open (and its OK button spinner cleared)
          // only when onOk rejects — swallowing here would silently
          // close the modal on failure, which spec §十四/CASE L forbid.
          throw e
        }
      },
    })
  }

  const projectActionMenuBusy = resolveProjectActionMenuTriggerBusy(creating, duplicating, deleting)

  // §三 — PROJECT_ACTION_MENU_ITEM_KEYS is the single source of truth
  // for "exactly these 3 actions, in this order"; this array literal
  // must stay in the same order as that contract constant.
  const projectActionMenuItems: MenuProps['items'] = readOnly
    ? [
        {
          key: PROJECT_ACTION_MENU_ITEM_KEYS[1],
          icon: <CopyOutlined />,
          label: t('contentProjects:team.canvas.copyProject'),
        },
      ]
    : [
    {
      key: PROJECT_ACTION_MENU_ITEM_KEYS[0],
      icon: <FileAddOutlined />,
      label: t('chrome.topBar.createProject'),
    },
    {
      key: PROJECT_ACTION_MENU_ITEM_KEYS[1],
      icon: <CopyOutlined />,
      label: t('chrome.topBar.duplicateProjectMenuItem'),
    },
    { type: 'divider' },
    {
      key: PROJECT_ACTION_MENU_ITEM_KEYS[2],
      icon: <DeleteOutlined />,
      label: t('chrome.topBar.deleteProjectMenuItem'),
      danger: true,
    },
  ]

  const handleProjectActionMenuClick: MenuProps['onClick'] = ({ key }) => {
    // §十八 — selecting any action closes the menu immediately (now
    // explicit since `open` is controlled below, no longer antd's own
    // implicit auto-close-on-item-click default).
    setMenuOpen(false)
    const target = resolveProjectActionMenuClickTarget(key)
    if (target === 'create') {
      void handleCreateProject()
    } else if (target === 'duplicate') {
      void handleDuplicateProject()
    } else if (target === 'delete') {
      handleDeleteProject()
    }
  }

  const recentContent = (
    <div className="pcv-recent-popover">
      <div className="pcv-recent-popover-title">{t('chrome.topBar.recentProjectsTitle')}</div>
      {recentLoading && !recentLoaded ? (
        <div className="pcv-recent-popover-loading">
          <Spin size="small" />
        </div>
      ) : recentItems.length === 0 ? (
        <div className="pcv-recent-popover-empty">{t('chrome.topBar.recentProjectsEmpty')}</div>
      ) : (
        <div className="pcv-recent-popover-list">
          {recentItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`pcv-recent-popover-item${item.id === projectId ? ' is-active' : ''}`}
              onClick={() => {
                setRecentOpen(false)
                if (item.id !== projectId) {
                  navigate(`/workspace/${workspaceId}/content-production/${item.id}/canvas`)
                }
              }}
            >
              <span className="pcv-recent-popover-item-title">{item.title}</span>
              <span className="pcv-recent-popover-item-time">{formatDateTime(item.updated_at, locale)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <div className="pcv-nav">
      <button
        type="button"
        className="pcv-nav-btn"
        onClick={goToProjectList}
        title={t('chrome.topBar.backToProjects')}
        aria-label={t('chrome.topBar.backToProjects')}
      >
        <LeftOutlined />
      </button>
      <Popover
        trigger="click"
        placement="bottomLeft"
        open={recentOpen}
        onOpenChange={(open) => {
          setRecentOpen(open)
          if (open) void loadRecent()
        }}
        content={recentContent}
        overlayClassName="pcv-recent-popover-overlay"
      >
        <button type="button" className="pcv-nav-btn" title={t('chrome.topBar.recentProjects')} aria-label={t('chrome.topBar.recentProjects')}>
          <ClockCircleOutlined />
        </button>
      </Popover>
      {renaming ? (
        <Input
          ref={inputRef}
          size="small"
          className="pcv-nav-title-input"
          value={draftTitle}
          maxLength={MAX_TITLE_LENGTH}
          disabled={savingTitle}
          autoFocus
          onChange={(e) => setDraftTitle(e.target.value)}
          onBlur={() => void commitRename()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void commitRename()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              cancelRename()
            }
          }}
        />
      ) : readOnly ? (
        <span className="pcv-nav-title pcv-nav-title--readonly" data-testid="pcv-nav-title-readonly" title={projectTitle}>
          {projectTitle}
        </span>
      ) : (
        <button
          type="button"
          className="pcv-nav-title"
          onClick={startRename}
          title={t('chrome.topBar.renameProject')}
          aria-label={t('chrome.topBar.renameProjectAria')}
        >
          {projectTitle}
        </button>
      )}
      <VistoeraActionMenu
        placement="bottomRight"
        open={menuOpen}
        onOpenChange={setMenuOpen}
        menu={{ items: projectActionMenuItems, onClick: handleProjectActionMenuClick }}
      >
        <button
          type="button"
          className="pcv-nav-project-menu-btn"
          disabled={projectActionMenuBusy}
          title={t('chrome.topBar.projectActions')}
          aria-label={t('chrome.topBar.projectActions')}
        >
          {projectActionMenuBusy ? <Spin size="small" /> : <MoreOutlined />}
        </button>
      </VistoeraActionMenu>
    </div>
  )
}
